/**
 * Arabic text reshaping utility for jsPDF.
 * jsPDF doesn't natively support Arabic connected letters or RTL.
 * This utility reshapes Arabic characters into their proper positional forms
 * and reverses the text for correct RTL display in PDF.
 */

// Arabic character forms: [isolated, initial, medial, final]
const ARABIC_FORMS: Record<number, number[]> = {
  0x0621: [0xFE80, 0xFE80, 0xFE80, 0xFE80], // ء HAMZA
  0x0622: [0xFE81, 0xFE81, 0xFE82, 0xFE82], // آ ALEF MADDA
  0x0623: [0xFE83, 0xFE83, 0xFE84, 0xFE84], // أ ALEF HAMZA ABOVE
  0x0624: [0xFE85, 0xFE85, 0xFE86, 0xFE86], // ؤ WAW HAMZA
  0x0625: [0xFE87, 0xFE87, 0xFE88, 0xFE88], // إ ALEF HAMZA BELOW
  0x0626: [0xFE89, 0xFE8B, 0xFE8C, 0xFE8A], // ئ YEH HAMZA
  0x0627: [0xFE8D, 0xFE8D, 0xFE8E, 0xFE8E], // ا ALEF
  0x0628: [0xFE8F, 0xFE91, 0xFE92, 0xFE90], // ب BEH
  0x0629: [0xFE93, 0xFE93, 0xFE94, 0xFE94], // ة TEH MARBUTA
  0x062A: [0xFE95, 0xFE97, 0xFE98, 0xFE96], // ت TEH
  0x062B: [0xFE99, 0xFE9B, 0xFE9C, 0xFE9A], // ث THEH
  0x062C: [0xFE9D, 0xFE9F, 0xFEA0, 0xFE9E], // ج JEEM
  0x062D: [0xFEA1, 0xFEA3, 0xFEA4, 0xFEA2], // ح HAH
  0x062E: [0xFEA5, 0xFEA7, 0xFEA8, 0xFEA6], // خ KHAH
  0x062F: [0xFEA9, 0xFEA9, 0xFEAA, 0xFEAA], // د DAL
  0x0630: [0xFEAB, 0xFEAB, 0xFEAC, 0xFEAC], // ذ THAL
  0x0631: [0xFEAD, 0xFEAD, 0xFEAE, 0xFEAE], // ر REH
  0x0632: [0xFEAF, 0xFEAF, 0xFEB0, 0xFEB0], // ز ZAIN
  0x0633: [0xFEB1, 0xFEB3, 0xFEB4, 0xFEB2], // س SEEN
  0x0634: [0xFEB5, 0xFEB7, 0xFEB8, 0xFEB6], // ش SHEEN
  0x0635: [0xFEB9, 0xFEBB, 0xFEBC, 0xFEBA], // ص SAD
  0x0636: [0xFEBD, 0xFEBF, 0xFEC0, 0xFEBE], // ض DAD
  0x0637: [0xFEC1, 0xFEC3, 0xFEC4, 0xFEC2], // ط TAH
  0x0638: [0xFEC5, 0xFEC7, 0xFEC8, 0xFEC6], // ظ ZAH
  0x0639: [0xFEC9, 0xFECB, 0xFECC, 0xFECA], // ع AIN
  0x063A: [0xFECD, 0xFECF, 0xFED0, 0xFECE], // غ GHAIN
  0x0640: [0x0640, 0x0640, 0x0640, 0x0640], // ـ TATWEEL
  0x0641: [0xFED1, 0xFED3, 0xFED4, 0xFED2], // ف FEH
  0x0642: [0xFED5, 0xFED7, 0xFED8, 0xFED6], // ق QAF
  0x0643: [0xFED9, 0xFEDB, 0xFEDC, 0xFEDA], // ك KAF
  0x0644: [0xFEDD, 0xFEDF, 0xFEE0, 0xFEDE], // ل LAM
  0x0645: [0xFEE1, 0xFEE3, 0xFEE4, 0xFEE2], // م MEEM
  0x0646: [0xFEE5, 0xFEE7, 0xFEE8, 0xFEE6], // ن NOON
  0x0647: [0xFEE9, 0xFEEB, 0xFEEC, 0xFEEA], // ه HEH
  0x0648: [0xFEED, 0xFEED, 0xFEEE, 0xFEEE], // و WAW
  0x0649: [0xFEEF, 0xFEEF, 0xFEF0, 0xFEF0], // ى ALEF MAKSURA
  0x064A: [0xFEF1, 0xFEF3, 0xFEF4, 0xFEF2], // ي YEH
};

// Characters that DON'T connect to the next character (right-joining only)
const RIGHT_JOIN_ONLY = new Set([
  0x0622, 0x0623, 0x0624, 0x0625, 0x0627, // آ أ ؤ إ ا
  0x0629, // ة
  0x062F, 0x0630, // د ذ
  0x0631, 0x0632, // ر ز
  0x0648, 0x0649, // و ى
  0x0621, // ء
]);

// LAM-ALEF ligatures
const LAM_ALEF: Record<number, number> = {
  0x0622: 0xFEF5, // لآ
  0x0623: 0xFEF7, // لأ
  0x0625: 0xFEF9, // لإ
  0x0627: 0xFEFB, // لا
};

function isArabicChar(code: number): boolean {
  return (code >= 0x0621 && code <= 0x064A) || code === 0x0640;
}

function isArabicDiacritic(code: number): boolean {
  return code >= 0x064B && code <= 0x065F;
}

/**
 * Reshapes Arabic text so that letters appear in their correct connected forms.
 * Also reverses the Arabic portions for proper RTL display in LTR PDF context.
 */
export function reshapeArabic(text: string): string {
  if (!text) return text;

  const chars = Array.from(text);
  const codes = chars.map(c => c.codePointAt(0)!);
  const result: number[] = [];

  for (let i = 0; i < codes.length; i++) {
    const code = codes[i];

    // Skip diacritics (tashkeel)
    if (isArabicDiacritic(code)) continue;

    if (!isArabicChar(code)) {
      result.push(code);
      continue;
    }

    // Check for LAM-ALEF ligature
    if (code === 0x0644 && i + 1 < codes.length) {
      const nextNonDiacritic = getNextNonDiacritic(codes, i + 1);
      if (nextNonDiacritic !== -1 && LAM_ALEF[codes[nextNonDiacritic]]) {
        const prevJoins = canPrevJoin(codes, result, i);
        const ligature = LAM_ALEF[codes[nextNonDiacritic]];
        result.push(prevJoins ? ligature + 1 : ligature);
        i = nextNonDiacritic; // Skip the alef
        continue;
      }
    }

    const forms = ARABIC_FORMS[code];
    if (!forms) {
      result.push(code);
      continue;
    }

    const prevJoins = canPrevJoin(codes, result, i);
    const nextJoins = canNextJoin(codes, i);

    let formIndex: number;
    if (prevJoins && nextJoins) {
      formIndex = 2; // medial
    } else if (prevJoins) {
      formIndex = 3; // final
    } else if (nextJoins) {
      formIndex = 1; // initial
    } else {
      formIndex = 0; // isolated
    }

    result.push(forms[formIndex]);
  }

  // Return reshaped text in logical order — PDF viewer handles RTL display
  return result.map(c => String.fromCodePoint(c)).join('');
}

function getNextNonDiacritic(codes: number[], start: number): number {
  for (let i = start; i < codes.length; i++) {
    if (!isArabicDiacritic(codes[i])) return i;
  }
  return -1;
}

function canPrevJoin(codes: number[], result: number[], currentIndex: number): boolean {
  // Look back for the previous Arabic character
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (isArabicDiacritic(codes[i])) continue;
    if (!isArabicChar(codes[i])) return false;
    // Previous char must be able to connect to the left (not right-join-only)
    return !RIGHT_JOIN_ONLY.has(codes[i]);
  }
  return false;
}

function canNextJoin(codes: number[], currentIndex: number): boolean {
  for (let i = currentIndex + 1; i < codes.length; i++) {
    if (isArabicDiacritic(codes[i])) continue;
    return isArabicChar(codes[i]);
  }
  return false;
}

function reverseArabicSegments(codes: number[]): string {
  const chars = codes.map(c => String.fromCodePoint(c));
  const result: string[] = [];
  let arabicBuffer: string[] = [];

  for (const ch of chars) {
    const code = ch.codePointAt(0)!;
    if (code > 0x0600 && code < 0xFF00 && code !== 0x0028 && code !== 0x0029) {
      arabicBuffer.push(ch);
    } else {
      if (arabicBuffer.length > 0) {
        result.push(...arabicBuffer.reverse());
        arabicBuffer = [];
      }
      result.push(ch);
    }
  }

  if (arabicBuffer.length > 0) {
    result.push(...arabicBuffer.reverse());
  }

  return result.join('');
}

/**
 * Checks if a string contains any Arabic characters.
 */
export function containsArabic(text: string): boolean {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text);
}

/**
 * True when the app's currently selected language is Arabic.
 */
export function isArabicLang(): boolean {
  return localStorage.getItem('lang') === 'ar';
}

/**
 * The legal-content endpoints return their markup double-encoded: a real wrapper
 * element around tags that were entity-escaped before being stored, e.g.
 * `<p>&lt;h2&gt;&lt;strong&gt;Terms&lt;/strong&gt;&lt;/h2&gt;...</p>`. Bound
 * straight to [innerHTML] that paints the tags on screen as literal text, and it
 * also hides the <h2> boundaries that the language filter below splits on.
 *
 * Decoding is applied once, and only when the string actually looks escaped, so
 * content that is already proper HTML passes through untouched.
 */
export function decodeEscapedHtml(html: string): string {
  if (!html || !/&lt;\s*\/?[a-z][^&]*&gt;/i.test(html)) return html;
  // A textarea's content is parsed as raw text: entities are decoded while any
  // genuine tags already in the string survive as-is.
  const decoder = document.createElement('textarea');
  decoder.innerHTML = html;
  return decoder.value;
}

/**
 * Some CMS-authored legal content (terms & conditions, privacy policy) stores the
 * English and Arabic translations back-to-back in a single HTML field instead of
 * two separate fields, so it always renders both languages mixed together. This
 * splits the content into blocks (each starting at an <h2>) and keeps only the
 * blocks whose script matches the requested language. Falls back to the full
 * content when nothing matches (e.g. the content isn't actually bilingual), so
 * nothing is ever silently blanked out.
 */
export function filterHtmlContentByLanguage(html: string, lang: string): string {
  if (!html) return html;
  const blocks = html.split(/(?=<h2[\s>])/i).filter((b) => b.trim().length > 0);
  if (blocks.length <= 1) return html;
  const wantArabic = lang === 'ar';
  const filtered = blocks.filter((block) => containsArabic(block) === wantArabic);
  return filtered.length > 0 ? filtered.join('') : html;
}

/**
 * Loads the bundled Amiri font into a jsPDF doc (idempotent \u2014 safe to call once per doc).
 * Required before any Arabic text can render correctly in a jsPDF-generated file.
 */
export async function ensureArabicFont(doc: any): Promise<boolean> {
  try {
    const fontName = 'Amiri';
    const fontFile = 'Amiri-Regular.ttf';
    const fontList = doc.getFontList?.() as Record<string, unknown> | undefined;
    if (fontList && fontList[fontName]) return true;

    let response = await fetch('/assets/fonts/Amiri-Regular.ttf');
    if (!response.ok) {
      response = await fetch('assets/fonts/Amiri-Regular.ttf');
    }
    if (!response.ok) return false;

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      return false;
    }

    const fontBuffer = await response.arrayBuffer();
    const header = new Uint8Array(fontBuffer, 0, 4);
    const isTTF = (header[0] === 0x00 && header[1] === 0x01 && header[2] === 0x00 && header[3] === 0x00) ||
                  (header[0] === 0x74 && header[1] === 0x72 && header[2] === 0x75 && header[3] === 0x65) ||
                  (header[0] === 0x4F && header[1] === 0x54 && header[2] === 0x54 && header[3] === 0x4F);
    if (!isTTF) return false;

    const bytes = new Uint8Array(fontBuffer);
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    const fontBase64 = btoa(binary);

    doc.addFileToVFS(fontFile, fontBase64);
    doc.addFont(fontFile, fontName, 'normal');
    doc.addFont(fontFile, fontName, 'bold');
    return true;
  } catch {
    return false;
  }
}

/**
 * jspdf-autotable `didParseCell` hook: right-aligns and reshapes any cell whose
 * raw text contains Arabic characters, regardless of the report's overall language
 * (so a UI-in-English report can still display an Arabic contest/user name correctly).
 * Pass as `didParseCell` in the table's config, or call from inside your own hook.
 */
export function arabicAwareCellHook(arabicFontLoaded: boolean) {
  return (data: any) => {
    if (data.section !== 'body') return;
    const raw = String(data.cell.raw ?? '');
    if (!containsArabic(raw)) return;
    data.cell.styles.halign = 'right';
    if (arabicFontLoaded) {
      data.cell.styles.font = 'Amiri';
      data.cell.text = [reshapeArabic(raw)];
    }
  };
}

/**
 * Renders a text string in a jsPDF doc, automatically switching to the Amiri font
 * and reshaping when the string contains Arabic \u2014 use for titles/headings/labels
 * drawn directly with doc.text() (autoTable cells should use arabicAwareCellHook instead).
 */
export function drawBilingualText(
  doc: any,
  text: string,
  x: number,
  y: number,
  arabicFontLoaded: boolean,
  options?: any
): void {
  if (containsArabic(text) && arabicFontLoaded) {
    const prevFont = doc.getFont();
    doc.setFont('Amiri', 'normal');
    doc.text(reshapeArabic(text), x, y, options);
    doc.setFont(prevFont.fontName, prevFont.fontStyle);
  } else {
    doc.text(text, x, y, options);
  }
}
